import { useState } from 'react'
import { Check, ChevronDown, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { toneClass, type Tone } from '@/components/ui/kpi-tile'

export interface ViewOption {
  value: string
  /** Tên mục — nói người dùng làm được gì ở đây, không phải tên kỹ thuật. */
  label: string
  /** Không còn hiển thị (bỏ chữ giải thích) — giữ cho nơi gọi cũ. */
  description?: string
  icon: LucideIcon
  tone: Tone
  count?: number
}

/**
 * Chuyển giữa các KHU VỰC của một trang (không phải bộ lọc danh sách — bộ lọc
 * dùng FilterTabs).
 *
 * Máy tính: dải tab gạch chân. Điện thoại: một thẻ "đang xem" (icon, tên, câu
 * giải thích); chạm để mở bảng chọn từ đáy màn hình liệt kê đủ các khu vực.
 * Không có thanh ngang cuộn/chia đều — nhãn bị cắt ngắn tới mức mất nghĩa.
 */
export function ViewSwitcher({
  value,
  onChange,
  options,
  title = 'Chọn mục',
  className,
}: {
  value: string
  onChange: (next: string) => void
  options: ViewOption[]
  /** Tiêu đề bảng chọn trên điện thoại. */
  title?: string
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const current = options.find((o) => o.value === value) ?? options[0]!

  return (
    <div className={cn('max-w-full', className)}>
      {/* Máy tính */}
      <div className="mobile:hidden">
        <div className="inline-flex max-w-full flex-wrap gap-1 border-b border-rule" role="tablist">
          {options.map((o) => {
            const active = o.value === value
            return (
              <button
                key={o.value}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => onChange(o.value)}
                className={cn(
                  'motion-state min-h-11 whitespace-nowrap border-b-2 px-3 text-sm',
                  active ? 'border-accent font-medium text-accent' : 'border-transparent text-inkSoft hover:text-ink',
                )}
              >
                {o.label}
                {o.count !== undefined && <span className="ml-1.5 tabular-nums text-muted">{o.count}</span>}
              </button>
            )
          })}
        </div>
      </div>

      {/* Điện thoại */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        className="motion-state hidden w-full items-center gap-3 rounded-xl bg-raise p-3 text-left shadow-card active:bg-paperSoft mobile:flex"
      >
        <span className={cn(toneClass(current.tone), 'grid size-10 shrink-0 place-items-center rounded-lg bg-toneBg text-toneFg')}>
          <current.icon className="size-5" aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-ink">
            {current.label}
            {current.count !== undefined && <span className="ml-1.5 font-normal tabular-nums text-muted">{current.count}</span>}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-0.5 text-xs font-medium text-accent">
          Đổi <ChevronDown className="size-4" aria-hidden />
        </span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="gap-2" aria-describedby={undefined}>
          {/* Tiêu đề chỉ cho trình đọc màn hình; các mục tự giải thích */}
          <DialogTitle className="sr-only">{title}</DialogTitle>
          <ul className="space-y-1.5 pt-7">
            {options.map((o) => {
              const active = o.value === value
              return (
                <li key={o.value}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(o.value)
                      setOpen(false)
                    }}
                    aria-current={active}
                    className={cn(
                      'motion-state flex w-full items-center gap-3 rounded-xl p-3 text-left',
                      active ? 'bg-accent/10 ring-1 ring-inset ring-accent/30' : 'hover:bg-paperSoft active:bg-paperSoft',
                    )}
                  >
                    <span className={cn(toneClass(o.tone), 'grid size-10 shrink-0 place-items-center rounded-lg bg-toneBg text-toneFg')}>
                      <o.icon className="size-5" aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-ink">
                        {o.label}
                        {o.count !== undefined && <span className="ml-1.5 font-normal tabular-nums text-muted">{o.count}</span>}
                      </span>
                    </span>
                    {active && <Check className="size-5 shrink-0 text-accent" aria-hidden />}
                  </button>
                </li>
              )
            })}
          </ul>
        </DialogContent>
      </Dialog>
    </div>
  )
}
